import { useTheme } from 'next-themes';
import {
  Award,
  Calendar,
  Clock,
  Coins,
  CreditCard,
  Database,
  EyeOff,
  Gauge,
  LayoutDashboard,
  MessageSquareText,
  Package,
  Plug,
  ShieldCheck,
  Sparkles,
  Store,
  TrendingUp,
  Upload,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { PAPER_CUT_ICON_NAMES, type PaperCutIconName } from './paperCutIconNames';

/**
 * Weiterhin von hier lesbar: `EmptyState.tsx` und `icons/index.ts` importieren
 * den Typ seit jeher aus dieser Datei. Ein reiner Typ-Reexport bricht Fast
 * Refresh nicht — die Regel sieht nur Wert-Exporte.
 */
export type { PaperCutIconName };

/**
 * Der Ersatz im Dunkelmodus, Motiv fuer Motiv.
 *
 * CLAUDE.md, Icon-System Stufe 2, sagt es seit jeher: "Paper Cut icons are for
 * light backgrounds only — on dark backgrounds use Lucide or the mono mark".
 * Die Regel stand nur nirgends im Code, und diese Komponente lieferte dieselbe
 * WebP-Datei in beiden Themes.
 *
 * Was das im Betrieb hiess, ist gemessen (09.09.2026, die beiden Motive, die
 * die Werkbank benutzt, per ffmpeg dekodiert und Pixel fuer Pixel gegen die
 * Kartenfarbe gerechnet): `quality-score` steht auf der dunklen Karte im
 * Median bei 1.42:1, 92 % seiner deckenden Pixel unter 3:1; `upload-import`
 * bei 1.68:1 und 99.7 % unter 3:1. Auf der hellen Karte sind es 8.79:1 und
 * 7.55:1. Die Motive sind in Markenpetrol geschnitten, und die dunkle Karte
 * IST Petrol — die 96 px grosse Illustration glaenzt also nicht, sie
 * verschwindet.
 *
 * Lucide statt der Wortmarke, obwohl CLAUDE.md beides erlaubt: die
 * Leerzustaende sagen "hier ist nichts zu pruefen" bzw. "hier ist etwas
 * hochzuladen". Die Marke bedeutet "FeedOptimizer" und traegt diese Aussage
 * nicht. Gezeichnet wird in `currentColor` — die Farbe folgt damit dem Theme,
 * statt wie eine Bilddatei in einem Ton festzustehen. Genau darum geht es.
 */
const DUNKEL_ERSATZ: Record<PaperCutIconName, LucideIcon> = {
  'ai-optimization': Sparkles,
  'feed-data': Database,
  product: Package,
  'speed-performance': Gauge,
  'analytics-growth': TrendingUp,
  // Das Paper-Cut-Motiv ist eine Rosette mit Baendern; `Award` ist dieselbe
  // Form in einer Linie.
  'quality-score': Award,
  'security-trust': ShieldCheck,
  'commerce-store': Store,
  'pricing-payment': CreditCard,
  'integration-connect': Plug,
  'upload-import': Upload,
  'schedule-calendar': Calendar,
  automation: Workflow,
  'multi-dashboard': LayoutDashboard,
  'custom-prompts': MessageSquareText,
  'problem-visibility': EyeOff,
  'problem-time': Clock,
  'problem-cost': Coins,
};

export interface PaperCutIconProps {
  name: PaperCutIconName;
  size?: number;
  alt?: string;
  className?: string;
}

/**
 * Paper Cut Flat illustration icon — generated with Nano 2.
 *
 * Use for feature sections, cards, hero areas (32-128px+).
 * For small UI icons (chevrons, X, check), use Lucide instead.
 *
 * Im Dunkelmodus weicht die Komponente selbst auf Lucide aus (Begruendung an
 * `DUNKEL_ERSATZ`). Die Regel sitzt hier und nicht bei den Aufrufern, weil sie
 * eine Eigenschaft DIESER Bilder ist: sie sind fuer helle Flaechen gemacht.
 */
export function PaperCutIcon({
  name,
  size = 64,
  alt,
  className,
}: PaperCutIconProps) {
  /**
   * `resolvedTheme` ist im ERSTEN Render `undefined` (next-themes loest es
   * nach dem Mount auf), und die App laeuft auf `defaultTheme="system"`. Ein
   * Nutzer mit dunklem Betriebssystem und ohne gespeicherte Wahl sieht also
   * einen Frame lang die WebP-Datei und holt sie einmal ab.
   *
   * Bewusst nicht behoben, weil jede Gegenmassnahme teurer ist als der Befund:
   * `undefined` als dunkel zu behandeln blitzt bei allen hellen Nutzern, gar
   * nichts zu rendern erzeugt einen Sprung im Layout. Ein Frame und ein
   * einmaliger 24-KB-Abruf sind der guenstigere Preis.
   */
  const { resolvedTheme } = useTheme();
  const defaultAlt = name
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  if (resolvedTheme === 'dark') {
    const Ersatz = DUNKEL_ERSATZ[name];
    // `alt=""` heisst in HTML "dekorativ, ueberspringen". Uebersetzt man das
    // stur nach `aria-label=""`, entsteht eine Grafik MIT Bildrolle und OHNE
    // Namen — schlechter als das `<img alt="">`, das sie ersetzt. Heute
    // uebergibt kein Aufrufer `alt`, aber die Prop ist oeffentlich.
    const beschriftung =
      alt === ''
        ? { 'aria-hidden': true as const }
        : { role: 'img', 'aria-label': alt ?? defaultAlt };
    return (
      <Ersatz
        // Denselben zugaenglichen Namen wie die Bilddatei: ein Leerzustand
        // soll seinem Motiv in beiden Themes gleich heissen. `<svg>` traegt
        // ohne `role` keine Rolle im Baum, das `aria-label` liefe sonst ins
        // Leere.
        {...beschriftung}
        width={size}
        height={size}
        // Lucides Standardstaerke 2 wirkt bei 96 px klobig; 1.5 ist die Staerke,
        // die die Bibliothek fuer grosse Groessen selbst vorschlaegt.
        strokeWidth={1.5}
        className={`paper-cut-icon text-primary ${className ?? ''}`}
      />
    );
  }

  return (
    <img
      src={`/images/icons/paper-cut/${name}.webp`}
      alt={alt ?? defaultAlt}
      width={size}
      height={size}
      className={`paper-cut-icon ${className ?? ''}`}
      loading="lazy"
      decoding="async"
    />
  );
}
